import React, { useState } from 'react';
import { Modal, StyleSheet, View } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { releaseMachine } from '@/entities/machine/api';
import { deleteStreamSource } from '@/entities/stream/api';
import { ApiError } from '@/shared/api';
import { messageOf } from '@/shared/lib/errors';
import { Banner } from '@/shared/ui/banner';
import { AppButton } from '@/shared/ui/button';
import { AppText } from '@/shared/ui/text';
import { radii, spacing, useTheme } from '@/shared/ui/theme';

/**
 * Remove a video recorder from the company.
 *
 * Not `DeleteCameraPanel`: a recorder came in by machine number (claim / adoption
 * code), so what keeps it in the company is the CLAIM and the per-company token the
 * claim handed it — deleting its stream source alone would leave the box holding a
 * live token, re-creating its cameras on its next snapshot. Two steps, in this order:
 *
 *   1. `POST /admin/machines/{number}/release` — revokes the token. The recorder hears
 *      401, drops it and goes back to showing a fresh adoption code on its screen.
 *   2. `DELETE /stream/sources/{peer}` — the box row, its channels, settings and
 *      command history. A 404 here means it is already gone, which is the goal.
 *
 * Both are idempotent, so a half-finished removal is healed by pressing again.
 */
const styles = StyleSheet.create({
  root: { gap: spacing(3) },
  backdrop: { flex: 1, justifyContent: 'center', padding: spacing(5) },
  dialog: { borderRadius: radii.xl, padding: spacing(5), gap: spacing(4) },
  row: { flexDirection: 'row', gap: spacing(2) },
  flex: { flex: 1 },
});

export function RemoveRecorderPanel({
  companyId,
  sourceId,
  machineNumber,
  name,
  onRemoved,
}: {
  companyId: string;
  sourceId: string;
  machineNumber: string | null;
  name: string;
  onRemoved: () => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const remove = useMutation({
    mutationFn: async () => {
      if (machineNumber) {
        await releaseMachine(machineNumber, companyId);
      }
      try {
        await deleteStreamSource(companyId, sourceId);
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 404)) {
          throw e;
        }
      }
    },
    onSuccess: () => {
      setConfirm(false);
      void qc.invalidateQueries({ queryKey: ['stream-cameras', companyId] });
      void qc.invalidateQueries({ queryKey: ['devices'] });
      onRemoved();
    },
  });

  return (
    <View style={styles.root}>
      <AppText variant="caption">{t('camera.recorder.remove.hint')}</AppText>
      <AppButton
        title={t('camera.recorder.remove.action')}
        tone="destructive"
        onPress={() => setConfirm(true)}
      />
      {remove.isError ? (
        <Banner
          tone="error"
          title={messageOf(remove.error, t('errors.unknown'))}
        />
      ) : null}

      <Modal
        visible={confirm}
        transparent
        animationType="fade"
        onRequestClose={() => setConfirm(false)}
      >
        <View style={[styles.backdrop, { backgroundColor: p.overlay }]}>
          <View style={[styles.dialog, { backgroundColor: p.elevated }]}>
            <AppText variant="title">
              {t('camera.recorder.remove.confirmTitle', { name })}
            </AppText>
            <AppText variant="body" color={p.mutedForeground}>
              {t('camera.recorder.remove.confirmBody')}
            </AppText>
            <View style={styles.row}>
              <AppButton
                title={t('actions.cancel')}
                tone="outline"
                style={styles.flex}
                onPress={() => setConfirm(false)}
              />
              <AppButton
                title={t('camera.recorder.remove.action')}
                tone="destructive"
                style={styles.flex}
                loading={remove.isPending}
                onPress={() => remove.mutate()}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
